/**
 * "New article from keyword" modal.
 *
 * Opens from the Articles grid header. Asks for a single keyword, calls
 * the factory endpoint, then navigates to the editor for the new draft.
 * If n8n isn't wired yet, surfaces the friendly setup message instead
 * of a bare error.
 */

import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { api, ApiError } from '../lib/api.ts'
import { friendlyError } from '../lib/friendly-errors.ts'

interface Props {
  onClose: () => void
}

export function NewArticleModal({ onClose }: Props) {
  const { siteId = 'chc' } = useParams()
  const navigate = useNavigate()
  const [keyword, setKeyword] = useState('')
  const [h1, setH1] = useState('')
  const [meta, setMeta] = useState('')
  const [busy, setBusy] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [errorHint, setErrorHint] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (keyword.trim().length < 3 && h1.trim().length < 3 && meta.trim().length < 3) return
    setBusy(true)
    setErrorMsg(null)
    setErrorHint(null)
    try {
      const res = await api<{ id: string; slug: string; status: string }>(
        `/api/sites/${siteId}/drafts/new`,
        {
          method: 'POST',
          body: JSON.stringify({
            keyword: keyword.trim(),
            h1: h1.trim() || undefined,
            meta: meta.trim() || undefined,
          }),
        },
      )
      // Navigate straight to the editor — the SSE stream will show factory progress.
      navigate(`/sites/${siteId}/articles/${res.slug}`)
      onClose()
    } catch (err) {
      const f = friendlyError(err instanceof ApiError ? err : (err as Error).message)
      setErrorMsg(f.message)
      setErrorHint(f.hint ?? null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(28, 25, 23, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        style={{
          background: 'var(--bg-elev)',
          borderRadius: 'var(--radius-lg)',
          padding: '1.5rem 1.75rem',
          maxWidth: 480,
          width: '90%',
          boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        }}
      >
        <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.15rem' }}>New article from a keyword</h2>
        <p style={{ margin: '0 0 1.25rem', color: 'var(--text-faint)', fontSize: '0.85rem' }}>
          Fill any of the three. Keyword alone: the writer chooses the headline. Headline or meta filled: the article uses your text exactly, guaranteed.
        </p>
        <form onSubmit={submit}>
          <input
            type="text"
            autoFocus
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="Keyword, e.g. fresh graduate salary malaysia (optional if you fill a field below)"
            style={{
              width: '100%',
              padding: '0.6rem 0.75rem',
              border: '1px solid var(--border-strong)',
              borderRadius: 'var(--radius)',
              fontSize: '0.95rem',
            }}
          />
          <input
            type="text"
            value={h1}
            onChange={(e) => setH1(e.target.value)}
            placeholder="Headline (H1), optional — if filled, the article must use it exactly"
            style={{ ...{
              width: '100%',
              padding: '0.6rem 0.75rem',
              border: '1px solid var(--border-strong)',
              borderRadius: 'var(--radius)',
              fontSize: '0.95rem',
            }, marginTop: '0.6rem' }}
          />
          <textarea
            value={meta}
            onChange={(e) => setMeta(e.target.value)}
            rows={2}
            placeholder="Meta description, optional — if filled, the article must deliver on it"
            style={{ ...{
              width: '100%',
              padding: '0.6rem 0.75rem',
              border: '1px solid var(--border-strong)',
              borderRadius: 'var(--radius)',
              fontSize: '0.95rem',
            }, marginTop: '0.6rem' }}
          />
          {errorMsg && (
            <div
              style={{
                marginTop: '0.75rem',
                padding: '0.6rem 0.8rem',
                borderRadius: 'var(--radius)',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                color: 'var(--danger)',
                fontSize: '0.85rem',
              }}
            >
              <strong>{errorMsg}</strong>
              {errorHint && <div style={{ marginTop: '0.25rem', color: 'var(--text-muted)', fontWeight: 400 }}>{errorHint}</div>}
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={busy || (keyword.trim().length < 3 && h1.trim().length < 3 && meta.trim().length < 3)}
            >
              {busy ? 'Drafting…' : 'Generate draft'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
